import { Cop31FactBlock, factFaqEntities } from "@/components/Cop31FactBlock";
import type { ReactNode } from "react";
import { InlineNewsletter } from "@/components/InlineNewsletter";
import { localePath } from "@/lib/locale-paths";
import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks } from "@/lib/seo";
import cop31Og from "@/assets/cop31-og.jpg.asset.json";
import { pageTitle } from "@/lib/brand";

const OG_IMAGE_URL = `https://akillisurdurulebilirlik.com${cop31Og.url}`;


// ---------- Route ----------
export const Route = createFileRoute("/cop31")({
  head: () => ({
    meta: [
      { title: pageTitle("COP31 Antalya 2026: Tarihler, Yer, Başkanlık ve Gündem", "tr") },
      {
        name: "description",
        content:
          "COP31 Antalya 2026: tarihler (9–20 Kasım), yer (Antalya EXPO), başkanlık, Liderler Zirvesi, Pre-COP ve gündem — bağımsız analiz notlarıyla.",
      },
      {
        name: "keywords",
        content:
          "COP31, COP31 Antalya, COP31 2026, COP31 Türkiye, iklim zirvesi 2026, UNFCCC COP31, Antalya iklim konferansı, CMA8, CMP21, Mavi Bölge, Yeşil Bölge, Türkiye iklim politikası",
      },
      { property: "og:title", content: "COP31 Antalya 2026: Tarihler, Yer, Başkanlık ve Gündem" },
      {
        property: "og:description",
        content:
          "COP31 Antalya 2026: tarihler (9–20 Kasım), yer (Antalya EXPO), başkanlık, Liderler Zirvesi, Pre-COP ve gündem — bağımsız analiz notlarıyla.",
      },
      { property: "og:type", content: "article" },
      { property: "article:published_time", content: "2026-05-01T00:00:00Z" },
      { property: "article:modified_time", content: "2026-07-03T00:00:00Z" },
      { property: "article:section", content: "İklim" },
      { property: "article:tag", content: "COP31" },
      { property: "article:tag", content: "İklim Politikası" },
      { property: "article:tag", content: "Antalya 2026" },
      { property: "og:url", content: "https://akillisurdurulebilirlik.com/cop31" },
      { property: "og:locale", content: "tr_TR" },
      { property: "og:locale:alternate", content: "en_US" },
      { property: "og:image", content: OG_IMAGE_URL },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "COP31 Antalya 2026: Tarihler, Yer, Başkanlık ve Gündem" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE_URL },
      { name: "twitter:title", content: "COP31 Antalya 2026: Tarihler, Yer, Başkanlık ve Gündem" },
      { name: "twitter:description", content: "COP31 Antalya 2026: tarihler (9–20 Kasım), yer (Antalya EXPO), başkanlık, Liderler Zirvesi, Pre-COP ve gündem — bağımsız analiz notlarıyla." },
    ],


    links: [
      { rel: "canonical", href: "https://akillisurdurulebilirlik.com/cop31" },
      ...hreflangLinks("/cop31", "/cop31-guide"),
    ],

    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Event",
          name: "COP31 — Birleşmiş Milletler İklim Değişikliği Konferansı 2026",
          alternateName: ["COP31 Antalya", "UN Climate Change Conference 2026", "CMA8", "CMP21"],
          description:
            "Birleşmiş Milletler İklim Değişikliği Çerçeve Sözleşmesi'nin 31. Taraflar Konferansı; Kyoto Protokolü Taraflar Toplantısı'nın 21. oturumu (CMP21) ve Paris Anlaşması Taraflar Toplantısı'nın 8. oturumu (CMA8) ile birlikte, Türkiye'nin ev sahipliğinde Antalya'da toplanıyor.",
          image: [OG_IMAGE_URL],
          startDate: "2026-11-09",
          endDate: "2026-11-20",
          eventStatus: "https://schema.org/EventScheduled",
          eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
          inLanguage: ["tr", "en"],
          location: {
            "@type": "Place",
            name: "Antalya EXPO Center, Aksu — Antalya, Türkiye",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Antalya",
              addressRegion: "Antalya",
              addressCountry: "TR",
            },
          },
          organizer: [
            {
              "@type": "Organization",
              name: "UNFCCC — Birleşmiş Milletler İklim Değişikliği Çerçeve Sözleşmesi Sekretaryası",
              url: "https://unfccc.int",
              sameAs: ["https://unfccc.int/cop31"],
            },
            {
              "@type": "GovernmentOrganization",
              name: "Türkiye Cumhuriyeti Çevre, Şehircilik ve İklim Değişikliği Bakanlığı",
              url: "https://csb.gov.tr",
              sameAs: ["https://iklim.gov.tr", "https://cop31.tr"],
            },
          ],
          sameAs: [
            "https://unfccc.int/cop31",
            "https://cop31.tr",
            "https://iklim.gov.tr",
            "https://en.wikipedia.org/wiki/2026_United_Nations_Climate_Change_Conference",
          ],
          url: "https://akillisurdurulebilirlik.com/cop31",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: "https://akillisurdurulebilirlik.com/" },
            { "@type": "ListItem", position: 2, name: "İklim & Sistemler", item: "https://akillisurdurulebilirlik.com/iklim-sistemleri" },
            { "@type": "ListItem", position: 3, name: "COP31 Rehberi", item: "https://akillisurdurulebilirlik.com/cop31" },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          inLanguage: "tr",
          mainEntity: [
            ...factFaqEntities("tr"),
            {
              "@type": "Question",
              name: "COP31 ne zaman ve nerede yapılacak?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "COP31, 9-20 Kasım 2026 tarihlerinde Türkiye'nin ev sahipliğinde Antalya EXPO Center'da (Aksu, Antalya) toplanacak.",
              },
            },
            {
              "@type": "Question",
              name: "COP31 nedir?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "COP31, Birleşmiş Milletler İklim Değişikliği Çerçeve Sözleşmesi'nin 31. Taraflar Konferansı'dır. Aynı çatı altında Kyoto Protokolü Taraflar Toplantısı'nın 21. oturumu (CMP21) ve Paris Anlaşması Taraflar Toplantısı'nın 8. oturumu (CMA8) da yürütülür.",
              },
            },
            {
              "@type": "Question",
              name: "COP31 başkanlığını kim üstleniyor?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "COP31 başkanlığını Türkiye üstleniyor; Başkan, Çevre, Şehircilik ve İklim Değişikliği Bakanı Murat Kurum. Türkiye-Avustralya Ortaklık Modaliteleri uyarınca Avustralya yakın müzakere ortağı olarak çalışıyor ve Pre-COP bir Pasifik ülkesinde yapılacak.",
              },
            },
            {
              "@type": "Question",
              name: "COP31'e nasıl katılınır?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Konferans iki bölgeye ayrılıyor. Mavi Bölge (UNFCCC'nin yönettiği resmi müzakere alanı) yalnızca akreditasyonla erişilebilir; taraf delegasyonları, kayıtlı gözlemci kuruluşlar, BM ajansları ve akredite medya için. Yeşil Bölge halka açık; kayıt cop31.tr üzerinden çevrimiçi yapılacak.",
              },
            },
            {
              "@type": "Question",
              name: "COP31'in resmi bilgi kaynakları nelerdir?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Uluslararası düzeyde unfccc.int/cop31, Türkiye Başkanlığı düzeyinde cop31.tr; kurumsal duyurular için iklim.gov.tr ve csb.gov.tr. COP31 adını taşıyan bağımsız siteler resmi değildir; akreditasyon ve kayıt işlemleri yalnızca bu adreslerden yürütülmelidir.",
              },
            },
          ],
        }),
      },
    ],

  }),
  component: Cop31Page,
});

import { PageIntro, PageSection, Prose, MoreLink, LinkRows, type RowItem } from "@/components/site/page";
import { cop31NotesSorted } from "@/lib/cop31-notes";

// Every Turkish COP31 note permalink, linked in server HTML (compact: title + date).
const NOTE_ROWS: RowItem[] = cop31NotesSorted
  .filter((n) => n.locale !== "en")
  .map((n) => ({ key: n.slug, to: "/cop31/notlar/$slug", params: { slug: n.slug }, title: n.titleTr, meta: n.dateTr }));

// ---------- Analiz katmanı data ----------
type AnalizItem = { title: string; date: string; url: string; excerpt: string };

const ANALIZ_ITEMS: AnalizItem[] = [
  {
    title: "COP31 ve iklim jeopolitiği: Üretim nerede kurulacak?",
    date: "2 Kasım 2026",
    url: "/yazilar/cop31-ve-iklim-jeopolitigi-uretim-nerede-kurulacak",
    excerpt:
      "İklim politikası artık bir üretim ve jeopolitik meselesi. COP31 öncesinde temiz teknoloji yatırımlarının coğrafyasını ve Türkiye'nin konumunu inceliyoruz.",
  },
  {
    title: "Cumhuriyet'in kalkınma idealinden yeni rekabet gücüne",
    date: "28 Ekim 2026",
    url: "/yazilar/cumhuriyet-in-kalkinma-idealinden-yeni-rekabet-gucune",
    excerpt:
      "Cumhuriyet'in kalkınma mirasını bugünün yeşil dönüşüm ve rekabet gücü tartışmalarına bağlayan bir değerlendirme.",
  },
  {
    title: "İklim finansmanı: Fayda net, hesap karışık",
    date: "27 Ekim 2026",
    url: "/yazilar/iklim-finansmani-fayda-net-hesap-karisik",
    excerpt:
      "İklim finansmanı tek bir havuz değil. Azaltım, geçiş, uyum ve kayıp-zarar ayrımının neden kurumsal bir model gerektirdiğini inceliyoruz.",
  },
  {
    title: "COP31, elektrik ekonomisi ve şebeke darboğazı",
    date: "21 Ekim 2026",
    url: "/yazilar/cop31-elektrik-ekonomisi-ve-sebeke-darbogazi",
    excerpt:
      "Enerji dönüşümünün gerçek sınırı üretim kapasitesi değil, şebeke altyapısı ve bağlantı kuyruklarıdır. COP31 Antalya için şebeke eylem karnesi önerisi.",
  },
  {
    title: "COP31: Türkiye'nin fırsatı, dönüşümün sınavı",
    date: "14 Ekim 2026",
    url: "/yazilar/cop31-turkiye-nin-firsati-donusumun-sinavi",
    excerpt:
      "Türkiye, COP31'de ekonomik ihtiyaçlarla iklim hedeflerini buluşturuyor. Antalya'nın fırsatı, ortak hedefleri somut adımlara dönüştürmek; sınavı ise bu adımların önündeki kilitleri açmak.",
  },
  {
    title: "COP31: İklimin siyasi ekonomisi",
    date: "7 Ekim 2026",
    url: "/cop31/notlar/cop31-iklimin-siyasi-ekonomisi",
    excerpt:
      "Paris Anlaşması, iklim diplomasisini karbon üzerine kurdu. Antalya onu güç üzerinden yeniden düşünmek zorunda. Çünkü Paris'in üzerine kurulduğu dünya artık yok.",
  },
  {
    title: "Antalya'dan önce son durak: Pasifik",
    date: "1 Ekim 2026",
    url: "/yazilar/antalya-dan-once-son-durak-pasifik",
    excerpt:
      "Fiji, Tuvalu ve Avustralya'nın ortaklaşa düzenlediği resmi Pre-COP süreci 5–8 Ekim'de gerçekleşecek. Pasifik'in Antalya'ya taşıdığı üç öncelik açık: 1,5°C hedefini masada tutmak, iklim finansmanına adil erişim ve okyanusu resmi müzakere gündemine taşımak.",
  },
  {
    title: "New York'ta iklimin yeni dili",
    date: "30 Eylül 2026",
    url: "https://www.ekonomigazetesi.com/kose-yazisi/new-yorkta-iklimin-yeni-dili-88600",
    excerpt:
      "New York İklim Haftası'nda Türkiye, COP31 için elektrifikasyondan yapay zekâya, finansmandan şehirlere uzanan bir uygulama gündemi ortaya koydu. Özel sektör de bu kez masadaydı. Antalya'ya giderken asıl soru artık ne vaat edileceği değil, neyin gerçekten uygulanabileceği olacak.",
  },
  {
    title: "COP31'in önündeki yeni fay hattı",
    date: "18 Eylül 2026",
    url: "/yazilar/cop31-in-onundeki-yeni-fay-hatti",
    excerpt:
      "ABD karbonu ekonominin üzerinden kaldırılması gereken bir yük olarak görüyor; Avrupa ise rekabetin yeni giriş koşuluna dönüştürüyor. Dünya, karbonun fiyatı üzerinden iki ayrı ekonomik düzene ayrılıyor. COP31'de ABD'nin sandalyesi boş kalabilir; ama karbonu ucuzlatan politikası masanın tam ortasında olacak.",
  },
  {
    title: "İklim hedefinden rekabet avantajına",
    date: "9 Eylül 2026",
    url: "https://www.ekonomigazetesi.com/kose-yazisi/iklim-hedefinden-rekabet-avantajina-87087",
    excerpt:
      "İklim dönüşümü artık yalnızca çevre politikasının değil, ekonomik rekabetin de merkezinde. İstanbul İklim Finansmanı Zirvesi'nde Türkiye COP31 Başkanlığı'nın öne çıkardığı İklim Uygulama Köprüsü (BRIDGE), hedeflerle finansman arasındaki kopukluğu — yatırım yapılabilir proje üretme kapasitesini — hedefliyor. Yıllık 7,5–9 trilyon dolarlık ihtiyaca karşılık yaklaşık 1,9 trilyon dolarlık yatırım, açığın yalnızca para değil, öngörülebilir politika ve yapılandırılmış proje meselesi olduğunu gösteriyor.",
  },
  {
    title: "Uçurumdan köprüye: İklim finansmanının yeni dili",
    date: "6 Eylül 2026",
    url: "/yazilar/ucurumdan-kopruye-iklim-finansmaninin-yeni-dili",
    excerpt:
      "İstanbul İklim Finansı Zirvesi'nin iki kelimesi — uçurum ve köprü — iki ayrı ikna rejimini anlatıyor: uçurum vicdanlara, köprü komitelere konuşuyor. COP31 öncesinde finansman açığının bir ucu paranın değil, bu iki dil arasındaki çevirinin eksikliği.",
  },
  {
    title: "Antalya'nın yeni sınavı: 1,5°C aşıldı!",
    date: "2 Eylül 2026",
    url: "/yazilar/antalya-nin-yeni-sinavi-1-5-c-asildi",
    excerpt:
      "UNEP'in Limiting Overshoot raporu, 1,5°C'yi aşılmaması gereken bir sınır olmaktan çıkarıp yüksekliği, süresi ve kimin kapatacağı belirlenmesi gereken bir aşım penceresine dönüştürüyor. COP31'in uygulama iddiası, bu aşımın geri ödeme takvimini yazıp yazamayacağıyla sınanacak.",
  },
  {
    title: "Karbon piyasası nedir?",
    date: "31 Ağustos 2026",
    url: "/yazilar/karbon-piyasasi-nedir",
    excerpt:
      "Emisyon Ticaret Sistemi (ETS) Yönetmeliği ile Türkiye'nin karbon piyasası kuruluyor: devlet toplam salıma bir üst sınır koyuyor, her biri bir ton karbona karşılık gelen tahsisatlar şirketlere dağıtılıyor ve sınırı aşan, açığını piyasadan satın alıyor. Tahsisatların bir bölümü ücretsiz veriliyor, kalanı birincil piyasada ihaleyle satılıyor; ikincil piyasada ise şirketler arasında alınıp satılıyor. Borsaya benzeyen ama kıtlığın bilinçli olarak yaratıldığı bir tasarım: piyasa istikrar rezervi ve fiyat aralıklarıyla birlikte, COP31 öncesinde Türkiye'nin elindeki en somut azaltım aracı.",
  },
  {
    title: "Mavi karbon nedir?",
    date: "19 Ağustos 2026",
    url: "https://www.ekonomigazetesi.com/kose-yazisi/mavi-karbon-nedir-85726",
    excerpt:
      "Bir deniz çayırı zarar gördüğünde yalnızca canlı bir habitatı kaybetmiyoruz; orada yıllar boyunca birikmiş karbonu da riske atıyoruz. Ekosistem bozulduğunda tortudaki organik karbon yeniden parçalanır ve karbonun bir bölümü suya ya da atmosfere geri döner. Mavi karbon bu nedenle COP31 gündeminin sessiz ama kritik başlığı: kıyı ekosistemleri hem uyum hem azaltım tarafında sayılabilir bir varlık, ölçüm ve koruma olmadıkça da geri dönüşü olmayan bir kayıp.",
  },
  {
    title: "COP31'e üç ay kala tablo nasıl?",
    date: "12 Ağustos 2026",
    url: "https://www.ekonomigazetesi.com/kose-yazisi/cop31e-uc-ay-kala-tablo-nasil-85191",
    excerpt:
      "9–20 Kasım'da Antalya'da yapılacak zirvenin çerçevesi büyük ölçüde belirginleşti: Türkiye ev sahibi ve Başkan, Avustralya ise Müzakereler Başkanı rolünde. Pre-COP31 bu kez 5–8 Ekim'de Fiji'de, ayrıca Tuvalu'da özel bir liderler buluşması var; hazırlık süreci Akdeniz–Pasifik hattında yürüyor. Bonn'da Küresel Uyum Hedefi (GGA) göstergeleri, metodoloji ve finansman bağlantısında uzlaşma sağlanamadı; dosyalar açık şekilde Antalya'ya kaldı. Böylece Antalya'nın önünde birbirine bağlı iki gündem var: resmi müzakereler ve Türkiye'nin Eylem Ajandası ile uygulama-yatırım gündemi.",
  },
  {
    title: "Yeşil aklama artık ahlâk değil, ispat meselesi!",
    date: "7 Ağustos 2026",
    url: "/yazilar/yesil-aklama-artik-ahlak-degil-ispat-meselesi",
    excerpt:
      "1 Ağustos'tan itibaren \"doğa dostu\", \"karbon nötr\" veya \"sürdürülebilir\" gibi ifadeler kanıt gerektiriyor. Yeşil aklama artık yalnızca etik bir eleştiri değil, hukuki bir ispat meselesi. COP31'in arka planında da bu dönüşüm var: taahhütlerin güvenilirliği, iddianın kanıtıyla ölçülecek.",
  },
  {
    title: "Yeşil Finans Stratejisi ne diyor?",
    date: "8 Temmuz 2026",
    url: "https://www.ekonomigazetesi.com/kose-yazisi/yesil-finans-stratejisi-ne-diyor-82719",
    excerpt:
      "Yayımlanan strateji ve eylem belgesinin, İklim Kanunu ile omuz omuza COP31 açısından kritik önemi burada. Ev sahibinin elindeki en güçlü diplomatik enstrüman bu. Belgenin vizyon cümlesindeki \"model ülke olmak\" iddiası ancak bu somutlukla taşınabilir. Aksi halde broşür cümlesi olarak kalır.",
  },
  {
    title: "Su gidince",
    date: "1 Temmuz 2026",
    url: "https://www.ekonomigazetesi.com/kose-yazisi/su-gidince-82218",
    excerpt:
      "Çocukluğumda, ailece pikniğe gitmek dört gözle beklediğim bir şeydi. Düzenli gittiğimiz bir yer vardı. Ankara'dan 45 dakika mesafede küçük bir gölün etrafı. Gölgelikli, suyu durgun, sessiz bir yer. Yıllar sonra, İstanbul'dan Ankara'ya bir gidişimde, orayı tekrar görmek istedim. Aynı yola girdim. Aynı virajları döndüm, gözüm o tanıdık yeri aradı. Ama göl yoktu. Yerinde çatlamış bir çukur, kurumuş bir yatak vardı. Su gitmişti. Onunla beraber, herkes.",
  },
  {
    title: "Yıkıcı bilanço: 30'a 1!",
    date: "6 Mayıs 2026",
    url: "https://www.ekonomigazetesi.com/kose-yazisi/yikici-bilanco-30a-1-78675",
    excerpt:
      "Doğayı korumaya ayrılan her 1 dolara karşılık, tahrip eden faaliyetlere yaklaşık 30 dolar akıyor. Toplamda 7,3 trilyon dolarlık tahrip edici finansman, yalnızca 220 milyar dolarlık koruma yatırımıyla karşılanıyor. Bu rakamlar, UNEP'in State of Finance for Nature 2026 raporundan. Sorun yalnızca ekonominin büyüme önceliğinde değil, kurumsal tasarımda: sürdürülebilirlik kavramı, sistemin doğaya ilişkin değer tanımını sorgulamak zorunda.",
  },
  {
    title: "Coğrafya kader değil, stratejidir",
    date: "29 Nisan 2026",
    url: "https://www.ekonomigazetesi.com/kose-yazisi/cografya-kader-degil-stratejidir-78122",
    excerpt:
      "Türkiye, iklim krizinin üç ana cephesinin kesiştiği bir coğrafyada: Akdeniz iklim kırılganlığı, enerji güvenliği ve finansman. Bu üç eksen birbirini hem besliyor hem de karmaşıklaştırıyor. COP31, Türkiye'ye bu kırılganlığı tutarlı bir bölgesel çerçeveye dönüştürme ve bunun liderliğini üstlenme fırsatını veriyor.",
  },
  {
    title: "Kırılgan yaşam üçgeni: Su, enerji, tarım",
    date: "25 Mart 2026",
    url: "https://www.ekonomigazetesi.com/kose-yazisi/kirilgan-yasam-ucgeni-su-enerji-tarim-75429",
    excerpt:
      "Türkiye'nin Ulusal Su Planı (2026–2035) yayımlandı. Hedefler, stratejiler, eylemler; verimlilik, tasarruf, dijitalleşme, koordinasyon. Amaç, su kaynaklarının ekolojik, ekonomik ve sosyal boyutlarıyla sürdürülebilir yönetimini güvence altına almak. Son derece önemli, çünkü konu sadece su değil; aynı zamanda enerji, gıda ve ekosistem meselesi.",
  },
];

const ANALIZ_ROWS: RowItem[] = ANALIZ_ITEMS.map((item) => ({
  key: item.title,
  href: item.url,
  title: item.title,
  excerpt: item.excerpt,
  meta: item.date,
}));

// ---------- Page ----------
function Cop31Page() {
  return (
    <>
      <PageIntro
        crumbs={[{ label: "COP31" }]}
        kicker="COP31 · Antalya · 9–20 Kasım 2026"
        dot="bg-theme-planet"
        title="COP31 Antalya: Müzakerenin İçini Okumak"
        lede={
          <>
            <p>
              Birleşmiş Milletler İklim Değişikliği Çerçeve Sözleşmesi'nin 31. Taraflar
              Konferansı, 9-20 Kasım 2026'da Türkiye'nin ev sahipliğinde Antalya'da
              toplanıyor. Bu sayfa iki iş yapıyor: konferansa dair doğrulanmış temel
              bilgileri resmi kaynaklarıyla sunmak ve müzakerelerin görünmeyen katmanlarını
              — finansman mimarisini, su gündemini, taahhüt-uygulama açığını — Akıllı
              Sürdürülebilirlik merceğinden okumak.
            </p>
            <p className="font-serif italic">Resmiyet başka yerde, analiz burada.</p>
          </>
        }
      />

      <PageSection>
        <Cop31FactBlock lang="tr" />
        <div className="mt-10 max-w-4xl">
          <InlineNewsletter />
        </div>
      </PageSection>

      <PageSection id="kunye" title="COP31 Nedir, Ne Zaman, Nerede?">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] lg:gap-12">
          <Prose>
            <p>
              COP31, tek bir konferans değil; aynı çatı altında toplanan üç resmi oturumun
              ortak adı: Çerçeve Sözleşme'nin 31. Taraflar Konferansı (COP31), Kyoto
              Protokolü Taraflar Toplantısı'nın 21. Oturumu (CMP21) ve Paris Anlaşması
              Taraflar Toplantısı'nın 8. Oturumu (CMA8). Üç müzakere hattı eşzamanlı yürür;
              Paris Anlaşması'nın uygulama kuralları CMA hattında şekillenir.
            </p>
            <div className="space-y-1 text-sm">
              <a
                href="https://unfccc.int/cop31"
                target="_blank"
                rel="noopener"
                className="block text-ink/70 underline underline-offset-2 hover:text-ink"
              >
                Kaynak: UNFCCC — COP31 Resmi Sayfası
              </a>
              <a
                href="https://iklim.gov.tr"
                target="_blank"
                rel="noopener"
                className="block text-ink/70 underline underline-offset-2 hover:text-ink"
              >
                Kaynak: İklim Değişikliği Başkanlığı
              </a>
            </div>
          </Prose>
          <dl className="divide-y divide-ink/10">
            <div className="grid gap-1 py-3 sm:grid-cols-[9rem_1fr]">
              <dt className="text-[10px] uppercase tracking-[0.18em] text-ink/60">Tarih</dt>
              <dd className="text-[15px] text-ink/85">9-20 Kasım 2026</dd>
            </div>
            <div className="grid gap-1 py-3 sm:grid-cols-[9rem_1fr]">
              <dt className="text-[10px] uppercase tracking-[0.18em] text-ink/60">Yer</dt>
              <dd className="text-[15px] text-ink/85">Antalya EXPO Center, Aksu — Antalya, Türkiye</dd>
            </div>
            <div className="grid gap-1 py-3 sm:grid-cols-[9rem_1fr]">
              <dt className="text-[10px] uppercase tracking-[0.18em] text-ink/60">
                Ev sahibi ve Başkanlık
              </dt>
              <dd className="text-[15px] text-ink/85">
                Türkiye (COP31 Başkanı: Çevre, Şehircilik ve İklim Değişikliği Bakanı Murat
                Kurum)
              </dd>
            </div>
            <div className="grid gap-1 py-3 sm:grid-cols-[9rem_1fr]">
              <dt className="text-[10px] uppercase tracking-[0.18em] text-ink/60">
                Müzakere ortaklığı
              </dt>
              <dd className="text-[15px] text-ink/85">
                Türkiye-Avustralya Ortaklık Modaliteleri uyarınca Avustralya, gelecek
                başkanlıkla yakın ortaklık içinde çalışıyor; Pre-COP bir Pasifik ülkesinde
                yapılacak.
              </dd>
            </div>
          </dl>
        </div>
      </PageSection>

      <PageSection
        id="analiz"
        title="COP31'i Akıllı Sürdürülebilirlik Merceğinden Okumak"
        aside="Analiz Katmanı"
      >
        <LinkRows items={ANALIZ_ROWS} />
        <div className="mt-8">
          <MoreLink to="/iklim-sistemleri">Tüm iklim yazıları — İklim &amp; Sistemler</MoreLink>
        </div>
      </PageSection>

      <PageSection id="notlar" title="COP31 notları" aside="Arşiv">
        <LinkRows items={NOTE_ROWS} withExcerpt={false} />
      </PageSection>
    </>
  );
}
